"use client";

import Link from "next/link";
import type { PortalSubscription, PortalSubscriptionPlan } from "../lib/clientPortalApi";
import { formatPrice } from "../lib/portalPresentation";
import { subscriptionSelectionHref } from "../lib/portalSubscriptionSelection";
import { usePortalTranslations } from "../i18n/client";
import { usePortalPreferences } from "./PortalPreferences";
import { SubscriptionDashboard } from "./PortalPersonalViews";
import styles from "./PortalSubscriptions.module.css";

export function PortalSubscriptions({ plans, subscriptions, linked, portalSlug, authenticated, loginEnabled, selectedPlanUuid }: {
  plans: PortalSubscriptionPlan[];
  subscriptions: PortalSubscription[];
  linked: boolean;
  portalSlug: string;
  authenticated: boolean;
  loginEnabled: boolean;
  selectedPlanUuid?: string | null;
}) {
  const t = usePortalTranslations();
  const { locale } = usePortalPreferences();
  // A return URL is only a selection hint; availability still comes from the current authorized catalogue.
  const selectedPlan = authenticated && selectedPlanUuid ? plans.find((plan) => plan.uuid === selectedPlanUuid) : null;
  const visiblePlans = selectedPlan ? [selectedPlan] : plans;
  const SelectLink = authenticated ? Link : "a";

  return <div className={styles.content}>
    {authenticated && selectedPlanUuid && !selectedPlan ? <p className={styles.empty} role="status">{t("portal.subscriptions.unavailablePlan")}</p> : null}
    <section aria-label={t("portal.subscriptions.catalog")}>
      {selectedPlan ? <Link href={`/${encodeURIComponent(portalSlug)}/subscriptions`} scroll={false} className={styles.back}><span aria-hidden="true">←</span> {t("portal.subscriptions.backToPlans")}</Link> : null}
      {visiblePlans.length ? <div className={`${styles.plans} ${selectedPlan ? styles.selection : ""}`}>{visiblePlans.map((plan) => <article key={plan.uuid} className={`${styles.plan} ${plan.highlighted || selectedPlan ? styles.highlighted : ""}`}>
        {plan.badgeLabel ? <span className={styles.badge}>{plan.badgeLabel}</span> : null}
        <div>
          {plan.groupName ? <p className={styles.group}>{plan.groupName}</p> : null}
          <h2>{plan.name}</h2>
          {plan.description ? <p className={styles.description}>{plan.description}</p> : null}
        </div>
        <dl className={styles.prices}>
          {plan.monthlyPrice !== null ? <div><dt>{t("portal.subscriptions.monthly")}</dt><dd>{formatPrice(plan.monthlyPrice, plan.currency, locale)}</dd></div> : null}
          {plan.annualTotalPrice !== null ? <div><dt>{t("portal.subscriptions.annual")}</dt><dd>{formatPrice(plan.annualTotalPrice, plan.currency, locale)}</dd></div> : null}
        </dl>
        {plan.benefits.length ? <ul>{plan.benefits.map((benefit, index) => <li key={`${index}-${benefit}`}>{benefit}</li>)}</ul> : null}
        {!selectedPlan && (authenticated || loginEnabled) ? <SelectLink href={subscriptionSelectionHref(portalSlug, plan.uuid, authenticated)} {...(authenticated ? { scroll: false, prefetch: false } : {})} className={`primary-button ${styles.select}`} aria-label={t("portal.subscriptions.selectNamed", { name: plan.name })}>{t("portal.subscriptions.select")} <span aria-hidden="true">→</span></SelectLink> : null}
      </article>)}</div> : <p className={styles.empty}>{t("portal.subscriptions.noPlans")}</p>}
    </section>
    {linked ? <section className={styles.personal}>
      <h2>{t("portal.subscriptions.title")}</h2>
      <SubscriptionDashboard subscriptions={subscriptions} />
    </section> : null}
  </div>;
}
